import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Send, Bot, User, Sparkles, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useTierAccess } from '../hooks/useTierAccess';
import { UpgradePrompt } from '../components/UpgradePrompt';

export default function ChatPage() {
  const { api, user } = useAuth();
  const { canAccess, isAdmin } = useTierAccess();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const hasAccess = isAdmin || canAccess('ai_chat');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await api().get('/chat/history');
        if (res.data.history?.length > 0) {
          const grouped = res.data.history.reduce((acc, msg) => {
            if (msg.session_id === res.data.history[0].session_id) {
              acc.push({ role: 'user', content: msg.message, timestamp: msg.timestamp });
              acc.push({ role: 'assistant', content: msg.response, timestamp: msg.timestamp });
            }
            return acc;
          }, []);
          setMessages(grouped.slice(-10));
          setSessionId(res.data.history[0]?.session_id);
        }
      } catch (err) {
        console.error('Failed to load chat history:', err);
      }
    };
    loadHistory();
  }, [api]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await api().post('/chat', {
        message: userMessage,
        session_id: sessionId
      });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      console.error('Chat error:', err);
      toast.error('Failed to get response. Please try again.');
      setMessages(prev => prev.slice(0, -1)); // Remove user message on error
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    "What types of DOB violations are most common in Manhattan?",
    "How do I resolve an open ECB violation?",
    "What's the difference between DOB and ECB violations?",
    "How long do I have to respond to a violation notice?"
  ];

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="chat-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Help</h1>
          <p className="text-zinc-400">Ask questions about NYC building compliance</p>
        </div>

        {!hasAccess ? (
          <div className="flex-1 glass-card rounded-xl flex items-center justify-center">
            <UpgradePrompt requiredTier="pro" feature="Help" />
          </div>
        ) : (
        /* Chat Container */
        <div className="flex-1 glass-card rounded-xl flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 rounded-full bg-indigo-600/20 flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-indigo-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">How can I help you today?</h3>
                <p className="text-zinc-400 text-sm mb-6 max-w-md">
                  I can help you understand NYC building violations, compliance requirements, and property data.
                </p>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setInput(q)}
                      className="text-sm text-left px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10 hover:border-indigo-500/50 transition-colors"
                      data-testid={`suggested-question-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${i}`}
                >
                  <div className={`flex gap-3 max-w-[80%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                      msg.role === 'user' ? 'bg-indigo-600' : 'bg-zinc-700'
                    }`}>
                      {msg.role === 'user' ? (
                        <User className="w-4 h-4 text-white" />
                      ) : (
                        <Bot className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <div className={msg.role === 'user' ? 'message-user p-4' : 'message-ai p-4'}>
                      <p className="text-sm text-white whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start" data-testid="loading-indicator">
                <div className="flex gap-3 max-w-[80%]">
                  <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="message-ai p-4">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-zinc-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-4">
            <form 
              onSubmit={(e) => { e.preventDefault(); handleSend(); }}
              className="flex gap-3"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about building compliance..."
                className="input-dark flex-1"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                disabled={loading || !input.trim()}
                className="btn-primary"
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
            <p className="text-xs text-zinc-500 mt-2 text-center">
              Powered by AI • Your plan: {user?.tier} tier
            </p>
          </div>
        </div>
        )}
      </div>
    </DashboardLayout>
  );
}
